/**
 * useXssGuard.js
 * --------------
 * A reusable hook that wraps any Formik `onSubmit` callback and blocks
 * the submission when an XSS payload is detected in the form values.
 *
 * Usage (in any component that uses useFormik):
 *
 *   import { useXssGuard } from "Hooks/useXssGuard";
 *
 *   const { guardedSubmit } = useXssGuard(handleMyFormSubmit);
 *
 *   useFormik({
 *     ...
 *     onSubmit: guardedSubmit,   // ← swap the original handler here
 *   });
 *
 * Or wrap the handler inline, without modifying the formik config:
 *
 *   const { checkXss } = useXssGuard();
 *
 *   const handleSubmit = (values) => {
 *     if (checkXss(values)) return;   // toast already shown, stop here
 *     doActualSubmit(values);
 *   };
 */

import { useCallback } from "react";
import { containsXSS } from "utils/sanitize";
import { useNotification } from "Hooks/NotificationManager";

const XSS_WARNING_MESSAGE =
  "Invalid input detected. Please remove any script or HTML tags and try again.";

/**
 * @param {Function} [originalSubmitFn]  Optional Formik onSubmit handler to wrap.
 * @returns {{ guardedSubmit: Function, checkXss: Function }}
 *   - `guardedSubmit` – drop-in replacement for the formik `onSubmit` prop.
 *   - `checkXss(values)` – standalone helper: returns `true` (and shows a toast)
 *      if XSS is detected, returns `false` when the data is clean.
 */
export const useXssGuard = (originalSubmitFn) => {
  const notification = useNotification();

  /**
   * checkXss
   * Inspect `values` for XSS payloads.
   * Returns true when XSS is found (toast is fired automatically).
   */
  const checkXss = useCallback(
    (values) => {
      if (containsXSS(values)) {
        notification.warning(XSS_WARNING_MESSAGE);
        return true; // XSS found – caller should abort
      }
      return false; // clean
    },
    [notification]
  );

  /**
   * guardedSubmit
   * A wrapped version of `originalSubmitFn` that first runs the XSS check.
   * If XSS is found the toast fires and the original handler is NOT called.
   */
  const guardedSubmit = useCallback(
    (values, formikHelpers) => {
      if (checkXss(values)) return;
      if (typeof originalSubmitFn === "function") {
        originalSubmitFn(values, formikHelpers);
      }
    },
    [checkXss, originalSubmitFn]
  );

  return { guardedSubmit, checkXss };
};

export default useXssGuard;
